import React, { useCallback, useMemo, useState } from "react";

import Box from "veneer/Box";
import Button from "veneer/Button";
import DataTable, {
  PageSize,
  createColumnHelper,
  createFilter,
  sorting,
} from "veneer/DataTable";
import { Colors } from "veneer/Theme";
import Typography, { OverflowTooltipTypography } from "veneer/Typography";

import { MEDIA_ENTRY_FOLDER } from "cortex/ui/src/fleet/constant/mediaLibrary";
import {
  contentFoldersSelector,
  contentFoldersStoresLoadedSelector,
} from "cortex/ui/src/fleet/redux-store/content-folders/selectors";
import {
  contentGroupsSelector,
  contentGroupsStoresLoadedSelector,
} from "cortex/ui/src/fleet/redux-store/content-groups/selectors";
import { addSpotsAction } from "cortex/ui/src/fleet/redux-store/ui-state/strategy";
import MediaTableFilters from "cortex/ui/src/fleet/view/media-library/component/MediaTableFilters";
import { formatPreview } from "cortex/ui/src/fleet/view/media-library/format";
import {
  Media,
  MediaLibraryStoreContentFolder,
} from "cortex/ui/src/fleet/view/media-library/model";
import {
  folderContentsToSpots,
  getTableData,
} from "cortex/ui/src/fleet/view/media-library/utils";
import { useAppDispatch, useAppSelector } from "cortex/ui/src/hooks";
import {
  applicationsSelector,
  applicationsStoresLoadedSelector,
} from "cortex/ui/src/redux-store/applications/selectors";
import { addSuccessMessageAction } from "cortex/ui/src/redux-store/messages";
import { getById } from "cortex/ui/src/redux-store/selectors";
import {
  userContentsSelector,
  userContentsStoresLoadedSelector,
} from "cortex/ui/src/redux-store/user-contents/selectors";
import { usersSelector } from "cortex/ui/src/redux-store/users/selectors";
import { formatDateTimeLocal } from "cortex/ui/src/ui/table/formats";

export type Filter = {
  tag?: string | null;
  type?: Media["friendlyType"] | null;
};

const filter = createFilter<Media>("displayName", "name");

const column = createColumnHelper<Media>();

interface AddSpotMediaTableProps {
  excludeColumns?: string[];
  handleSystemStrategyChange?: (rows: ReadonlyArray<any>) => void;
  hasFilters?: boolean;
  includeMediaTypes?: string[];
  initialPageSize?: PageSize;
  priority?: "main" | "fallback";
}

export default function AddSpotMediaTable({
  excludeColumns,
  handleSystemStrategyChange,
  hasFilters,
  includeMediaTypes,
  initialPageSize = 10,
  priority,
}: AddSpotMediaTableProps) {
  const dispatch = useAppDispatch();
  const [filters, setFilters] = useState<Filter>(() => ({
    tag: null,
    type: null,
  }));
  const contentFolders = useAppSelector(contentFoldersSelector);
  const contentFoldersLoaded = useAppSelector(
    contentFoldersStoresLoadedSelector,
  );
  const contentGroups = useAppSelector(contentGroupsSelector);
  const contentGroupsLoaded = useAppSelector(contentGroupsStoresLoadedSelector);
  const userContents = useAppSelector(userContentsSelector);
  const userContentsLoaded = useAppSelector(userContentsStoresLoadedSelector);
  const applications = useAppSelector(applicationsSelector);
  const applicationsStoresLoaded = useAppSelector(
    applicationsStoresLoadedSelector,
  );
  const users = useAppSelector(usersSelector);
  const storesLoaded = useMemo(
    () =>
      applicationsStoresLoaded &&
      contentFoldersLoaded &&
      contentGroupsLoaded &&
      userContentsLoaded,
    [
      applicationsStoresLoaded,
      contentFoldersLoaded,
      contentGroupsLoaded,
      userContentsLoaded,
    ],
  );

  const allRows = useMemo(() => {
    if (!storesLoaded) {
      return [];
    }
    return getTableData({
      applications: { byId: applications },
      contentFolders,
      contentGroups,
      includeArchived: false,
      includeMediaTypes: includeMediaTypes || [],
      userContents: userContents as any,
    }) as ReadonlyArray<Media>;
  }, [
    applications,
    contentFolders,
    contentGroups,
    includeMediaTypes,
    storesLoaded,
    userContents,
  ]);

  const visibleRows = useMemo(() => {
    return allRows.filter(
      ({ friendlyType, tag }) =>
        (!filters.tag || tag === filters.tag) &&
        (!filters.type || friendlyType === filters.type),
    );
  }, [allRows, filters]);

  const typeOptions = useMemo(
    () =>
      Array.from(
        new Set(
          visibleRows
            .map((row) => row.friendlyType)
            .filter((type): type is NonNullable<Media["friendlyType"]> =>
              Boolean(type),
            ),
        ),
      ).sort(),
    [visibleRows],
  );

  const addMediaEntryToStrategy = useCallback(
    (mediaEntry: Media, priority?: "main" | "fallback") => {
      const { id, type } = mediaEntry;

      const spots: ReadonlyArray<Media> =
        type === MEDIA_ENTRY_FOLDER
          ? (() => {
              const folder =
                id == null
                  ? null
                  : (getById(
                      contentFolders,
                      id,
                    ) as MediaLibraryStoreContentFolder | null);
              return folder
                ? ([
                    ...folderContentsToSpots(
                      applications,
                      userContents as any,
                      contentGroups,
                      folder,
                    ),
                  ] as ReadonlyArray<Media>)
                : [];
            })()
          : [mediaEntry];
      const strategySpots = [...spots] as any;

      if (handleSystemStrategyChange) {
        handleSystemStrategyChange(strategySpots);
        return;
      }

      dispatch(
        addSpotsAction({ priority: priority ?? "main", spots: strategySpots }),
      );
      dispatch(addSuccessMessageAction("Success! Added to strategy."));
    },
    [
      applications,
      contentFolders,
      contentGroups,
      handleSystemStrategyChange,
      userContents,
      dispatch,
    ],
  );

  const columns = useMemo(
    () => [
      column.accessor("url", {
        header: "Preview",
        cell: (prop) =>
          formatPreview(prop.getValue(), prop.row.original as any),
        enableSorting: false,
        meta: {
          align: "center",
          width: 125,
        },
      }),
      column.accessor("displayName", {
        header: "Name",
        cell: (prop) => (
          <OverflowTooltipTypography variant="body2">
            {prop.getValue() ?? "Untitled"}
          </OverflowTooltipTypography>
        ),
        sortingFn: "alphanumeric",
      }),
      column.accessor("friendlyType", {
        header: "Type",
        cell: (prop) => prop.getValue() || "N/A",
        sortingFn: "alphanumeric",
        meta: {
          width: 150,
        },
      }),
      column.accessor("upload_date", {
        header: "Uploaded",
        cell: (prop) => formatDateTimeLocal(prop.getValue()),
        sortingFn: sorting.datetime((row: Media) => row.upload_date),
        meta: {
          width: 200,
        },
      }),
      column.accessor("user_id", {
        header: "Uploaded By",
        cell: (prop) => {
          const userId = prop.getValue();
          const user = users[userId || ""];
          return user ? (
            <>
              {user?.name}
              <br />
              {`<${user.email}>`}
            </>
          ) : (
            ""
          );
        },
        sortingFn: sorting.number((row: Media) => row.user_id || 0),
        meta: {
          width: "fit-content",
        },
      }),
      column.display({
        id: "actions",
        header: "Actions",
        cell: (prop) => {
          const title =
            prop.row.original.type === MEDIA_ENTRY_FOLDER
              ? "Add all of this folder's contents to the strategy"
              : "Add this spot to the strategy";
          return (
            <Button
              icon="add"
              onClick={() => {
                addMediaEntryToStrategy(prop.row.original, priority);
              }}
              title={title}
              variant="outlined"
            >
              Add
            </Button>
          );
        },
        meta: {
          width: 150,
        },
      }),
    ],
    [addMediaEntryToStrategy, priority, users],
  );

  const filterOptions = useMemo(
    () => (
      <MediaTableFilters
        onChange={setFilters}
        typeOptions={typeOptions}
        value={filters}
      />
    ),
    [filters, typeOptions],
  );

  return (
    <Box ml={-2.5} mr={-1.5}>
      <DataTable
        columns={columns}
        emptyState={
          <Box p={2} textAlign="center">
            <Typography color={Colors.gray1000} variant="body2">
              There is no data to display
            </Typography>
          </Box>
        }
        filter={filter}
        hideColumns={excludeColumns}
        initialPageSize={initialPageSize}
        initialSort={{ id: "upload_date", desc: true }}
        paginationProps={{ rowsPerPageOptions: [5, 10, 25, 50, 100] }}
        rows={visibleRows as ReadonlyArray<Media>}
        viewOptions={hasFilters && filterOptions}
      />
    </Box>
  );
}
